export interface JobCode {
	id: string;
	name: string;
	color: string;
}

export interface TimeOffReason {
	'@id': string;
	'@type': string;
	id: number;
	name: string;
	description: string | null;
	requiresApproval: boolean;
	active: boolean;
	deleted: boolean;
	created_at: string | null;
	updated_at: string | null;
}

// For create/update requests
export interface TimeOffReasonRequest {
	name: string;
	description: string;
	isPaid: boolean;
	requiresApproval: boolean;
}

export interface Employee {
	id: string;
	name: string;
}

export interface ScheduleShift {
	id: string;
	employeeId: string;
	jobCodeId: string;
	date: string; // ISO string
	startTime: string; // HH:mm format
	endTime: string; // HH:mm format
}

export type ShiftFormData = Omit<ScheduleShift, 'id'>;

// Time increment in minutes for shift times
export const TIME_INCREMENT = 15;

// General Settings
export interface GeneralSettings {
	'@id'?: string;
	'@type'?: string;
	id?: number;
	workplace: string | null;
	ageOfMinor: number;
	overtimeDailyHoursAt: number;
	overtimeDailyWarningAt: number;
	overtimeWeeklyHoursAt: number;
	timeIncrement: number;
	overtimeWeeklyWarningAt: number;
	defaultShiftLengthHours: number;
	maxConsecutiveWorkDays?: number;
	workWeekStartDay?: string;
	allowOpenShifts?: boolean;
	minShiftLength: number;
	maxShiftLength: number;
}

export interface GeneralSettingsRequest {
	workplace: string | null;
	ageOfMinor: number;
	overtimeDailyHoursAt: number;
	overtimeDailyWarningAt: number;
	overtimeWeeklyHoursAt: number;
	timeIncrement: number;
	overtimeWeeklyWarningAt: number;
	defaultShiftLengthHours: number;
	maxConsecutiveWorkDays?: number;
	workWeekStartDay?: string;
	allowOpenShifts?: boolean;
	minShiftLength: number;
	maxShiftLength: number;
}

// Schedule Resource Types
export interface ScheduleJobCode {
	id: number | string;
	'@id': string;
	label: string;
	jobNumber?: string;
	backgroundColor: string;
	textColor: string;
}

export interface ScheduleEmployee {
	id: number | string;
	'@id': string;
	firstName: string;
	lastName: string;
}

// Base resource type that extends MbscResource
export interface BaseScheduleResource {
	id: number | string;
	name: string;
	color?: string;
}

// Job Code Resource (used in Day view)
export interface JobCodeResource extends BaseScheduleResource {
	jobCode: ScheduleJobCode;
	// No employee data in job code resources
	employee?: never;
	isOpenShift?: never;
	totalHours?: never;
	hasOvertime?: never;
	hasDailyOvertime?: never;
	hasWeeklyOvertime?: never;
	isApproachingWeeklyOvertime?: never;
	hasTimeOff?: never;
	hasAvailabilityIssue?: never;
	isMinor?: never;
	overtimeHours?: never;
	weeklyOTWarning?: never;
	displayName?: never;
}

// Employee Resource (used in Week view)
export interface EmployeeResource extends BaseScheduleResource {
	employee: ScheduleEmployee;
	displayName: string;
	totalHours: number;
	hasOvertime?: boolean;
	hasDailyOvertime?: boolean;
	hasWeeklyOvertime?: boolean;
	isApproachingWeeklyOvertime?: boolean;
	hasTimeOff?: boolean;
	hasAvailabilityIssue?: boolean;
	isMinor?: boolean;
	overtimeHours?: number;
	weeklyOvertimeHours?: number;
	totalDailyOvertimeHours?: number;
	weeklyOTWarning?: number;
	// No job code in employee resources
	jobCode?: never;
	isOpenShift?: never;
}

// Open Shift Resource (used in Week view)
export interface OpenShiftResource extends BaseScheduleResource {
	isOpenShift: true;
	displayName: string;
	totalHours?: number;
	// No employee or job code data
	employee?: never;
	jobCode?: never;
	hasOvertime?: never;
	hasDailyOvertime?: never;
	hasWeeklyOvertime?: never;
	isApproachingWeeklyOvertime?: never;
	hasTimeOff?: never;
	hasAvailabilityIssue?: never;
	isMinor?: never;
	overtimeHours?: never;
	weeklyOTWarning?: never;
}

// Event Type Resource (used in Event calendar view)
export interface EventTypeResource extends BaseScheduleResource {
	// Only has id and name from base
	employee?: never;
	jobCode?: never;
	isOpenShift?: never;
	displayName?: never;
	totalHours?: never;
	hasOvertime?: never;
	hasDailyOvertime?: never;
	hasWeeklyOvertime?: never;
	isApproachingWeeklyOvertime?: never;
	hasTimeOff?: never;
	hasAvailabilityIssue?: never;
	isMinor?: never;
	overtimeHours?: never;
	weeklyOTWarning?: never;
}

// Union type for all possible resource types
export type ScheduleResource =
	| JobCodeResource
	| EmployeeResource
	| OpenShiftResource
	| EventTypeResource;

// Type guard functions
export function isJobCodeResource(resource: ScheduleResource): resource is JobCodeResource {
	return 'jobCode' in resource && resource.jobCode !== undefined;
}

export function isEmployeeResource(resource: ScheduleResource): resource is EmployeeResource {
	return 'employee' in resource && resource.employee !== undefined && !('isOpenShift' in resource);
}

export function isOpenShiftResource(resource: ScheduleResource): resource is OpenShiftResource {
	return 'isOpenShift' in resource && resource.isOpenShift === true;
}

export function isEventTypeResource(resource: ScheduleResource): resource is EventTypeResource {
	return !('employee' in resource) && !('jobCode' in resource) && !('isOpenShift' in resource);
}
